import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight,
  Brain,
  Fingerprint,
  GitBranch,
  Network,
  Orbit,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Waypoints,
  Zap,
} from 'lucide-react';
import NetworkBackground from '@/components/clusterscope/NetworkBackground';
import WalletNetworkBackground from '@/components/clusterscope/WalletNetworkBackground';
import type { Dataset } from '@/data/clusterscope-data';

interface CountUpStatProps {
  value: number;
  decimals: number;
  prefix: string;
  suffix: string;
  label: string;
  delay?: number;
}

function CountUpStat({ value, decimals, prefix, suffix, label, delay = 0 }: CountUpStatProps) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start: number | null = null;
    let raf = 0;
    const duration = 1500;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const timeout = setTimeout(() => {
      const step = (ts: number) => {
        if (start === null) start = ts;
        const progress = Math.min((ts - start) / duration, 1);
        setDisplay(value * ease(progress));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [value, delay]);

  return (
    <div className="flex flex-col items-center justify-center px-4 py-4 text-center">
      <div className="text-2xl font-extrabold tabular-nums text-foreground sm:text-3xl">
        {prefix}
        {display.toFixed(decimals)}
        {suffix}
      </div>
      <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

const FEATURE_PILLS = [
  { icon: Waypoints, label: 'Cluster Graph Visualization' },
  { icon: Route, label: 'Funding Trail Tracing' },
  { icon: Fingerprint, label: 'Behavioral Fingerprinting' },
  { icon: RefreshCw, label: 'Real-time Network Detection' },
];

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface LandingScreenProps {
  dataset: Dataset;
  onBeginAnalysis: () => void;
  onScanWallet: (address: string) => void;
}

/* ── Reusable atmosphere primitives ── */

function SectionReveal({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: easeOut }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({ badge, title, subtitle }: { badge?: string; title: string; subtitle?: string }) {
  return (
    <div className="mx-auto mb-14 max-w-2xl text-center">
      {badge && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, ease: easeOut }}
          className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_2px_hsl(var(--primary)/0.35)]" />
          {badge}
        </motion.div>
      )}
      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, ease: easeOut }}
        className="text-[clamp(1.6rem,3.5vw,2.4rem)] font-extrabold leading-[1.12] tracking-tight text-foreground"
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.08, ease: easeOut }}
          className="mx-auto mt-4 max-w-lg text-sm text-muted-foreground sm:text-base"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

function LargeNetworkPattern({ color = 'teal' }: { color?: 'teal' | 'violet' }) {
  const c = color === 'teal' ? '#14e0b0' : '#a78bfa';
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" style={{ opacity: 0.03 }} fill="none" preserveAspectRatio="none">
      {/* Nodes */}
      <circle cx="10%" cy="25%" r="2" fill={c} fillOpacity="0.5" />
      <circle cx="30%" cy="15%" r="1.5" fill={c} fillOpacity="0.5" />
      <circle cx="55%" cy="20%" r="2" fill={c} fillOpacity="0.5" />
      <circle cx="80%" cy="18%" r="1.5" fill={c} fillOpacity="0.5" />
      <circle cx="90%" cy="75%" r="2" fill={c} fillOpacity="0.5" />
      <circle cx="65%" cy="82%" r="1.5" fill={c} fillOpacity="0.5" />
      <circle cx="40%" cy="78%" r="2" fill={c} fillOpacity="0.5" />
      <circle cx="15%" cy="70%" r="1.5" fill={c} fillOpacity="0.5" />
      <circle cx="50%" cy="48%" r="1.5" fill={c} fillOpacity="0.4" />
      <circle cx="25%" cy="50%" r="1" fill={c} fillOpacity="0.4" />
      <circle cx="75%" cy="50%" r="1" fill={c} fillOpacity="0.4" />
      {/* Connections */}
      <line x1="10%" y1="25%" x2="30%" y2="15%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="30%" y1="15%" x2="55%" y2="20%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="55%" y1="20%" x2="80%" y2="18%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="80%" y1="18%" x2="90%" y2="75%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="90%" y1="75%" x2="65%" y2="82%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="65%" y1="82%" x2="40%" y2="78%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="40%" y1="78%" x2="15%" y2="70%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="15%" y1="70%" x2="10%" y2="25%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="30%" y1="15%" x2="50%" y2="48%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="55%" y1="20%" x2="50%" y2="48%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="50%" y1="48%" x2="65%" y2="82%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      <line x1="50%" y1="48%" x2="40%" y2="78%" stroke={c} strokeWidth="0.5" strokeOpacity="0.4" />
      {/* Wallet-like labels */}
      <text x="10%" y="25%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">7xKj...</text>
      <text x="30%" y="15%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">9mNp...</text>
      <text x="55%" y="20%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">2vQw...</text>
      <text x="80%" y="18%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">5rSt...</text>
      <text x="90%" y="75%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">8uVx...</text>
      <text x="65%" y="82%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">3yZa...</text>
      <text x="40%" y="78%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">1bCd...</text>
      <text x="15%" y="70%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.3" fontSize="5" fontFamily="'JetBrains Mono', monospace">4eFg...</text>
      <text x="50%" y="48%" dy="10" textAnchor="middle" fill={c} fillOpacity="0.25" fontSize="5" fontFamily="'JetBrains Mono', monospace">6hIj...</text>
    </svg>
  );
}

function DriftingParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    let w = 0;
    let h = 0;

    const particles = Array.from({ length: 40 }, () => ({
      x: 0,
      y: 0,
      vx: (Math.random() - 0.5) * 0.15,
      vy: -Math.random() * 0.2 - 0.05,
      size: Math.random() * 1 + 0.5,
      opacity: Math.random() * 0.04 + 0.04,
      color: Math.random() > 0.85 ? '#14e0b0' : '#a78bfa',
    }));

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      particles.forEach((p) => {
        if (p.x === 0 && p.y === 0) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
        }
      });
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -10) p.y = h + 10;
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-[1]" />;
}

/* ── How It Works ── */

const STEPS = [
  {
    num: '01',
    title: 'Analyze',
    body: 'Enter a token or wallet address to start your investigation.',
    icon: Search,
  },
  {
    num: '02',
    title: 'Discover',
    body: 'Our engine maps connected wallets, funding trails, and behavioural clusters.',
    icon: Zap,
  },
  {
    num: '03',
    title: 'Investigate',
    body: 'Dive deep into wallet intelligence, capital trace, and network graphs.',
    icon: ShieldCheck,
  },
];

function HowItWorks() {
  return (
    <section className="relative w-full px-4 py-20 sm:py-24">
      <LargeNetworkPattern color="teal" />
      {/* Stronger teal accent glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#14e0b0] opacity-[0.025] blur-[120px]" />
      <div className="pointer-events-none absolute left-[15%] top-[20%] h-[200px] w-[200px] rounded-full bg-[#14e0b0] opacity-[0.02] blur-[80px]" />

      <SectionReveal>
        <SectionHeading badge="How It Works" title="Three Steps to Intelligence" />
        <div className="relative z-10 mx-auto grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 28, filter: 'blur(4px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: easeOut }}
              className="group relative flex flex-col items-center rounded-2xl p-4 text-center transition-all duration-500 hover:bg-white/[0.025]"
            >
              {i < STEPS.length - 1 && (
                <div className="pointer-events-none absolute right-0 top-10 hidden h-px w-full -translate-x-1/2 translate-x-[50%] bg-gradient-to-r from-transparent via-primary/15 to-transparent sm:block" />
              )}
              <div className="relative mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-2xl border border-primary/25 bg-primary/5 text-primary shadow-[0_0_24px_-4px_rgba(20,224,176,0.12)] backdrop-blur-sm transition-all duration-500 group-hover:border-primary/50 group-hover:shadow-[0_0_40px_0px_rgba(20,224,176,0.35)] group-hover:bg-primary/[0.08]">
                <div className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{ boxShadow: 'inset 0 0 0 1px rgba(20,224,176,0.35), 0 0 20px -4px rgba(20,224,176,0.25)' }} />
                <step.icon className="relative z-10 h-7 w-7" />
                <span className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-background text-[10px] font-bold text-primary ring-1 ring-primary/30">
                  {step.num}
                </span>
              </div>
              <h3 className="mb-2 text-base font-bold text-foreground">{step.title}</h3>
              <p className="max-w-[260px] text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </motion.div>
          ))}
        </div>
      </SectionReveal>
    </section>
  );
}

/* ── Example Analysis ── */

function ExampleAnalysis() {
  return (
    <section className="relative w-full px-4 py-20 sm:py-24">
      <LargeNetworkPattern color="violet" />
      <div className="pointer-events-none absolute right-0 top-0 h-[200px] w-[200px] rounded-full bg-[#a78bfa] opacity-[0.01] blur-[100px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[200px] w-[200px] rounded-full bg-[#14e0b0] opacity-[0.01] blur-[100px]" />

      <SectionReveal>
        <SectionHeading
          badge="Example Analysis"
          title="See What ClusterScope Reveals"
          subtitle="A preview of the intelligence surfaced during a typical token investigation."
        />
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="mx-auto max-w-4xl"
        >
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.4, ease: easeOut }}
            className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c10]/80 shadow-[0_0_60px_-12px_rgba(0,0,0,0.6)] backdrop-blur-xl transition-all duration-500 hover:border-white/[0.14] hover:shadow-[0_0_80px_-12px_rgba(20,224,176,0.08)]"
          >
            {/* Animated border glow */}
            <motion.div
              className="pointer-events-none absolute inset-0 rounded-2xl"
              animate={{ boxShadow: ['0 0 0px rgba(20,224,176,0)', '0 0 40px -8px rgba(20,224,176,0.07)', '0 0 0px rgba(20,224,176,0)'] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Inner hover gradient */}
            <div
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
              style={{
                background: 'radial-gradient(ellipse at 50% 0%, rgba(20,224,176,0.02), transparent 60%)',
              }}
            />

            {/* Preview frame header */}
            <div className="relative flex items-center gap-2 border-b border-white/[0.06] px-5 py-3">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/50" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/50" />
                <span className="h-2.5 w-2.5 rounded-full bg-green-500/50" />
              </div>
              <div className="ml-3 flex flex-1 items-center justify-center">
                <div className="rounded-md bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-wide text-muted-foreground">
                  clusterscope.app / ANSEM
                </div>
              </div>
            </div>

            {/* Polished preview visual */}
            <div className="relative flex items-center justify-center bg-[#08080c] px-6 py-10 sm:py-14">
              <svg
                className="w-full max-w-[720px]"
                viewBox="0 0 720 360"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                  </pattern>
                  <radialGradient id="glowTeal" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#14e0b0" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#14e0b0" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="glowViolet" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.14" />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
                  </radialGradient>
                  <filter id="blurSmall">
                    <feGaussianBlur stdDeviation="2" />
                  </filter>
                  <linearGradient id="panelGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.04)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.01)" />
                  </linearGradient>
                </defs>

                <rect width="720" height="360" fill="#08080c" />
                <rect width="720" height="360" fill="url(#grid)" />

                {/* Ambient glow behind graph */}
                <circle cx="210" cy="180" r="130" fill="url(#glowTeal)" />
                <circle cx="210" cy="180" r="100" fill="url(#glowViolet)" />

                {/* ===== LEFT: Cluster Graph ===== */}

                {/* Connection lines */}
                <g opacity="0.10">
                  <line x1="210" y1="180" x2="120" y2="110" stroke="#14e0b0" strokeWidth="1" />
                  <line x1="210" y1="180" x2="300" y2="110" stroke="#14e0b0" strokeWidth="1" />
                  <line x1="210" y1="180" x2="120" y2="250" stroke="#14e0b0" strokeWidth="1" />
                  <line x1="210" y1="180" x2="300" y2="250" stroke="#14e0b0" strokeWidth="1" />
                  <line x1="120" y1="110" x2="70" y2="145" stroke="#a78bfa" strokeWidth="0.8" />
                  <line x1="300" y1="110" x2="350" y2="145" stroke="#a78bfa" strokeWidth="0.8" />
                  <line x1="120" y1="250" x2="70" y2="215" stroke="#a78bfa" strokeWidth="0.8" />
                  <line x1="300" y1="250" x2="350" y2="215" stroke="#a78bfa" strokeWidth="0.8" />
                  <line x1="210" y1="180" x2="210" y2="70" stroke="#14e0b0" strokeWidth="0.8" />
                  <line x1="210" y1="180" x2="210" y2="290" stroke="#14e0b0" strokeWidth="0.8" />
                  <line x1="120" y1="110" x2="170" y2="140" stroke="#a78bfa" strokeWidth="0.6" />
                  <line x1="300" y1="110" x2="250" y2="140" stroke="#a78bfa" strokeWidth="0.6" />
                  <line x1="120" y1="250" x2="170" y2="220" stroke="#a78bfa" strokeWidth="0.6" />
                  <line x1="300" y1="250" x2="250" y2="220" stroke="#a78bfa" strokeWidth="0.6" />
                </g>

                {/* Secondary nodes */}
                <circle cx="70" cy="145" r="5" fill="#a78bfa" fillOpacity="0.18" />
                <circle cx="350" cy="145" r="5" fill="#a78bfa" fillOpacity="0.18" />
                <circle cx="70" cy="215" r="5" fill="#a78bfa" fillOpacity="0.18" />
                <circle cx="350" cy="215" r="5" fill="#a78bfa" fillOpacity="0.18" />
                <circle cx="210" cy="70" r="4.5" fill="#14e0b0" fillOpacity="0.15" />
                <circle cx="210" cy="290" r="4.5" fill="#14e0b0" fillOpacity="0.15" />
                <circle cx="170" cy="140" r="4" fill="#a78bfa" fillOpacity="0.14" />
                <circle cx="250" cy="140" r="4" fill="#a78bfa" fillOpacity="0.14" />
                <circle cx="170" cy="220" r="4" fill="#a78bfa" fillOpacity="0.14" />
                <circle cx="250" cy="220" r="4" fill="#a78bfa" fillOpacity="0.14" />

                {/* Primary ring nodes */}
                <circle cx="120" cy="110" r="7" fill="#a78bfa" fillOpacity="0.30" />
                <circle cx="300" cy="110" r="7" fill="#a78bfa" fillOpacity="0.30" />
                <circle cx="120" cy="250" r="7" fill="#a78bfa" fillOpacity="0.30" />
                <circle cx="300" cy="250" r="7" fill="#a78bfa" fillOpacity="0.30" />

                {/* Central hub */}
                <circle cx="210" cy="180" r="16" fill="#14e0b0" fillOpacity="0.10" filter="url(#blurSmall)" />
                <circle cx="210" cy="180" r="11" fill="#14e0b0" fillOpacity="0.38" />
                <circle cx="210" cy="180" r="4.5" fill="#14e0b0" fillOpacity="0.95" />

                {/* Pulse rings */}
                <circle cx="210" cy="180" r="24" stroke="#14e0b0" strokeOpacity="0.12" strokeWidth="1" fill="none" />
                <circle cx="210" cy="180" r="34" stroke="#14e0b0" strokeOpacity="0.06" strokeWidth="0.5" fill="none" />

                {/* Node labels */}
                <text x="210" y="169" textAnchor="middle" fill="#14e0b0" fillOpacity="0.65" fontSize="8" fontWeight="600" fontFamily="'JetBrains Mono', monospace">HUB</text>
                <text x="120" y="95" textAnchor="middle" fill="#a78bfa" fillOpacity="0.50" fontSize="7" fontFamily="'JetBrains Mono', monospace">7xKj...a3B</text>
                <text x="300" y="95" textAnchor="middle" fill="#a78bfa" fillOpacity="0.50" fontSize="7" fontFamily="'JetBrains Mono', monospace">9mNp...c4D</text>
                <text x="120" y="272" textAnchor="middle" fill="#a78bfa" fillOpacity="0.50" fontSize="7" fontFamily="'JetBrains Mono', monospace">2vQw...e5F</text>
                <text x="300" y="272" textAnchor="middle" fill="#a78bfa" fillOpacity="0.50" fontSize="7" fontFamily="'JetBrains Mono', monospace">5rSt...g6H</text>

                {/* Floating pill — top left */}
                <g transform="translate(24, 24)">
                  <rect x="0" y="0" width="120" height="26" rx="6" fill="rgba(20,224,176,0.08)" stroke="rgba(20,224,176,0.20)" strokeWidth="0.5" />
                  <circle cx="12" cy="13" r="3" fill="#14e0b0" fillOpacity="0.7" />
                  <text x="20" y="16" fill="#14e0b0" fillOpacity="0.85" fontSize="9" fontWeight="600" fontFamily="'JetBrains Mono', monospace">Cluster Detected</text>
                </g>

                {/* ===== DIVIDER ===== */}
                <line x1="375" y1="30" x2="375" y2="330" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                {/* ===== RIGHT: Insights Panel ===== */}

                {/* Panel background */}
                <rect x="385" y="24" width="310" height="312" rx="10" fill="url(#panelGrad)" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />

                {/* Panel header */}
                <text x="405" y="50" fill="#ffffff" fillOpacity="0.90" fontSize="12" fontWeight="700" fontFamily="'Inter', sans-serif">Smart Money</text>

                {/* Subtitle / token */}
                <text x="405" y="68" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="'JetBrains Mono', monospace">$BONK · 0x72...9f1a</text>

                {/* Divider */}
                <line x1="405" y1="80" x2="675" y2="80" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                {/* Metrics grid — 2 columns */}
                {/* Row 1 */}
                <text x="405" y="104" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">PnL (30d)</text>
                <text x="405" y="120" fill="#14e0b0" fillOpacity="0.90" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">+$1.24M</text>

                <text x="540" y="104" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">Win Rate</text>
                <text x="540" y="120" fill="#ffffff" fillOpacity="0.90" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">78%</text>

                {/* Row 2 */}
                <text x="405" y="148" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">Cluster Size</text>
                <text x="405" y="164" fill="#ffffff" fillOpacity="0.90" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">12 Wallets</text>

                <text x="540" y="148" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">Connected Wallets</text>
                <text x="540" y="164" fill="#ffffff" fillOpacity="0.90" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">47 Wallets</text>

                {/* Row 3 */}
                <text x="405" y="192" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">Funding Sources</text>
                <text x="405" y="208" fill="#a78bfa" fillOpacity="0.85" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">3 Origins</text>

                <text x="540" y="192" fill="rgba(255,255,255,0.40)" fontSize="8" fontWeight="500" fontFamily="'Inter', sans-serif">Confidence</text>
                <text x="540" y="208" fill="#14e0b0" fillOpacity="0.90" fontSize="13" fontWeight="700" fontFamily="'JetBrains Mono', monospace">94.2%</text>

                {/* Divider */}
                <line x1="405" y1="225" x2="675" y2="225" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                {/* Behavioural Summary */}
                <text x="405" y="244" fill="rgba(255,255,255,0.50)" fontSize="8" fontWeight="600" fontFamily="'Inter', sans-serif" letterSpacing="0.08em">BEHAVIOURAL SUMMARY</text>

                <text x="405" y="264" fill="rgba(255,255,255,0.55)" fontSize="9" fontFamily="'Inter', sans-serif">
                  <tspan x="405" dy="0">Disciplined early entries with high</tspan>
                  <tspan x="405" dy="14">win-rate exits. Wallets show minimal</tspan>
                  <tspan x="405" dy="14">overlap in funding and consistent</tspan>
                  <tspan x="405" dy="14">profit-taking behaviour. Likely insider.</tspan>
                </text>


              </svg>
            </div>
          </motion.div>
        </motion.div>
      </SectionReveal>
    </section>
  );
}

/* ── Why ClusterScope ── */

function WhyClusterScope() {
  return (
    <section className="relative w-full px-4 py-20 sm:py-24">
      <LargeNetworkPattern color="violet" />
      {/* Stronger violet accent glow */}
      <div className="pointer-events-none absolute right-[10%] top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-[#a78bfa] opacity-[0.025] blur-[120px]" />
      <div className="pointer-events-none absolute left-[5%] top-[15%] h-[220px] w-[220px] rounded-full bg-[#a78bfa] opacity-[0.015] blur-[90px]" />

      {/* Faint background network graph */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" style={{ opacity: 0.025 }} fill="none" preserveAspectRatio="none">
        <circle cx="20%" cy="30%" r="3" fill="#a78bfa" fillOpacity="0.5" />
        <circle cx="50%" cy="20%" r="2.5" fill="#a78bfa" fillOpacity="0.5" />
        <circle cx="80%" cy="35%" r="3" fill="#a78bfa" fillOpacity="0.5" />
        <circle cx="35%" cy="70%" r="2.5" fill="#a78bfa" fillOpacity="0.5" />
        <circle cx="65%" cy="75%" r="3" fill="#a78bfa" fillOpacity="0.5" />
        <circle cx="50%" cy="50%" r="2" fill="#14e0b0" fillOpacity="0.4" />
        <line x1="20%" y1="30%" x2="50%" y2="20%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="50%" y1="20%" x2="80%" y2="35%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="20%" y1="30%" x2="35%" y2="70%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="80%" y1="35%" x2="65%" y2="75%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="35%" y1="70%" x2="50%" y2="50%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="65%" y1="75%" x2="50%" y2="50%" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="50%" y1="20%" x2="50%" y2="50%" stroke="#14e0b0" strokeWidth="0.5" strokeOpacity="0.2" />
      </svg>

      <SectionReveal>
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-10 sm:flex-row sm:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -24, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: easeOut }}
            className="flex-1"
          >
            <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_2px_hsl(var(--primary)/0.35)]" />
              Why ClusterScope
            </div>
            <h2 className="mb-4 text-[clamp(1.5rem,3vw,2rem)] font-extrabold leading-[1.15] tracking-tight text-foreground">
              See What Traditional<br />Explorers Miss
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
              Blockchain explorers show individual transactions. ClusterScope reveals the relationships behind them — coordinated wallet clusters, hidden funding paths, and behavioural fingerprints that expose the full picture.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: easeOut }}
            className="relative flex-1"
          >
            <motion.div
              whileHover={{ y: -3 }}
              transition={{ duration: 0.4, ease: easeOut }}
              className="group relative rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 backdrop-blur-xl transition-all duration-500 hover:border-white/[0.12] hover:shadow-[0_0_40px_-10px_rgba(167,139,250,0.04)]"
            >
              <div className="space-y-4">
                <ComparisonRow label="Individual TXs" status="missed" />
                <ComparisonRow label="Wallet Relationships" status="caught" />
                <ComparisonRow label="Funding Origins" status="caught" />
                <ComparisonRow label="Behavioural Clusters" status="caught" />
                <ComparisonRow label="Real-time Detection" status="caught" />
              </div>
              {/* Decorative accent line — pulsing */}
              <motion.div
                className="pointer-events-none absolute -left-px top-8 h-24 w-px bg-gradient-to-b from-primary/60 via-primary/20 to-transparent"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Hover glow */}
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  background: 'radial-gradient(500px circle at 50% 0%, rgba(167,139,250,0.02), transparent 60%)',
                }}
              />
            </motion.div>
            {/* Glow */}
            <div className="pointer-events-none absolute -right-16 -top-12 h-48 w-48 rounded-full bg-[#a78bfa] opacity-[0.04] blur-[80px]" />
          </motion.div>
        </div>
      </SectionReveal>
    </section>
  );
}

function ComparisonRow({ label, status }: { label: string; status: 'missed' | 'caught' }) {
  const isCaught = status === 'caught';
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-foreground/90">{label}</span>
      <span className={`flex items-center gap-1.5 text-xs font-semibold ${isCaught ? 'text-primary' : 'text-muted-foreground/60'}`}>
        {isCaught ? (
          <>
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_1px_rgba(20,224,176,0.25)]" />
            Revealed
          </>
        ) : (
          <>
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
            Missed
          </>
        )}
      </span>
    </div>
  );
}

/* ── Core Features ── */

const CORE_FEATURES = [
  {
    icon: Network,
    title: 'Wallet Clustering',
    body: 'Detect coordinated wallet groups and behavioural clusters invisible to standard analysis.',
    color: '#14e0b0',
  },
  {
    icon: GitBranch,
    title: 'Capital Trace',
    body: 'Trace funding origins across multiple hops and wallets to uncover the full money trail.',
    color: '#a78bfa',
  },
  {
    icon: Brain,
    title: 'Wallet Intelligence',
    body: 'Deep profile analysis with risk scoring, behavioural tags, and trading style classification.',
    color: '#14e0b0',
  },
  {
    icon: Orbit,
    title: 'Interactive Network Visualization',
    body: 'Explore connections through dynamic force-directed graphs with real-time filtering.',
    color: '#a78bfa',
  },
];

function CoreFeatures() {
  return (
    <section className="relative w-full px-4 py-20 sm:py-24">
      <LargeNetworkPattern color="teal" />
      {/* Stronger teal accent glow */}
      <div className="pointer-events-none absolute right-0 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-[#a78bfa] opacity-[0.015] blur-[120px]" />
      <div className="pointer-events-none absolute left-[10%] top-[20%] h-[280px] w-[280px] rounded-full bg-[#14e0b0] opacity-[0.025] blur-[110px]" />

      <SectionReveal>
        <SectionHeading badge="Core Features" title="Built for Serious Investigators" />
        <div className="relative z-10 mx-auto grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-2">
          {CORE_FEATURES.map((feat, i) => (
            <motion.div
              key={feat.title}
              initial={{ opacity: 0, y: 28, filter: 'blur(4px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: easeOut }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 backdrop-blur-sm transition-all duration-500 hover:border-white/[0.14] hover:bg-white/[0.035] hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.5)]"
            >
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl"
                style={{ boxShadow: `inset 0 1px 0 0 ${feat.color}08` }}
              />
              <div
                className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  background: `radial-gradient(420px circle at 50% 0%, ${feat.color}06, transparent 60%)`,
                }}
              />
              {/* Enhanced hover glow overlay */}
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  boxShadow: `inset 0 1px 0 0 ${feat.color}08`,
                }}
              />
              <div className="relative z-10">
                <div
                  className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-xl border transition-all duration-500 group-hover:scale-105"
                  style={{
                    borderColor: `${feat.color}25`,
                    backgroundColor: `${feat.color}10`,
                    color: feat.color,
                    boxShadow: `inset 0 1px 0 0 ${feat.color}10, 0 0 12px -2px ${feat.color}12`,
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{ boxShadow: `0 0 20px -4px ${feat.color}30, inset 0 1px 0 0 ${feat.color}15` }}
                  />
                  <feat.icon className="relative z-10 h-6 w-6" />
                </div>
                <h3 className="mb-2 text-base font-bold text-foreground">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{feat.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </SectionReveal>
    </section>
  );
}

/* ── Final CTA ── */

function FinalCTA({ onBeginAnalysis }: { onBeginAnalysis: () => void }) {
  return (
    <section className="relative w-full px-4 py-28 sm:py-36">
      <LargeNetworkPattern color="teal" />
      {/* Most dramatic balanced glows */}
      <div className="pointer-events-none absolute left-1/4 top-1/2 h-[450px] w-[450px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#14e0b0] opacity-[0.03] blur-[130px]" />
      <div className="pointer-events-none absolute right-1/4 top-1/2 h-[450px] w-[450px] translate-x-1/2 -translate-y-1/2 rounded-full bg-[#a78bfa] opacity-[0.03] blur-[130px]" />
      <div className="pointer-events-none absolute left-1/2 top-[30%] h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-[#14e0b0] opacity-[0.015] blur-[100px]" />

      <SectionReveal>
        <div className="relative mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: easeOut }}
          >
            <h2 className="mb-4 text-[clamp(1.6rem,3.5vw,2.4rem)] font-extrabold leading-[1.12] tracking-tight text-foreground">
              Start Uncovering Hidden Networks
            </h2>
            <p className="mx-auto mb-8 max-w-md text-sm text-muted-foreground sm:text-base">
              Analyze tokens, profile wallets, and trace capital flows in seconds.
            </p>
            <motion.button
              onClick={onBeginAnalysis}
              whileHover={{ scale: 1.035 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2, ease: easeOut }}
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-[#14e0b0] px-8 py-3.5 text-sm font-bold text-[#04140f] shadow-[0_0_24px_-2px_rgba(20,224,176,0.30)] transition-shadow hover:shadow-[0_0_48px_4px_rgba(20,224,176,0.45)]"
            >
              <motion.div
                className="pointer-events-none absolute inset-0 z-0"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                Begin Analysis
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </motion.button>
          </motion.div>

          {/* Glow accents */}
          <div className="pointer-events-none absolute -left-32 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-[#14e0b0] opacity-[0.06] blur-[80px]" />
          <div className="pointer-events-none absolute -right-32 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-[#a78bfa] opacity-[0.06] blur-[80px]" />
        </div>
      </SectionReveal>
    </section>
  );
}

/* ── Main component ── */

export default function LandingScreen({ dataset, onBeginAnalysis, onScanWallet }: LandingScreenProps) {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });
  const { scrollY } = useScroll();
  const orbParallax1 = useTransform(scrollY, [0, 3000], [0, -180]);
  const orbParallax2 = useTransform(scrollY, [0, 3000], [0, -270]);
  const orbParallax3 = useTransform(scrollY, [0, 3000], [0, 150]);
  const orbParallax4 = useTransform(scrollY, [0, 3000], [0, -90]);
  const orbParallax5 = useTransform(scrollY, [0, 3000], [0, 90]);
  const networkOpacity = useTransform(scrollY, [0, 2000], [1, 0.25]);

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-hidden bg-background">
      {/* Background layers — fixed so they persist through all sections */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-grid-pattern opacity-[0.35]" />
      <motion.div style={{ opacity: networkOpacity }} className="pointer-events-none fixed inset-0 z-0">
        <NetworkBackground />
      </motion.div>
      <WalletNetworkBackground />
      <DriftingParticles />

      {/* Grain / noise texture overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-[3] opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Slow animated radial lighting overlay */}
      <motion.div
        animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        className="pointer-events-none fixed inset-0 z-[2]"
        style={{
          background: 'radial-gradient(circle at 30% 30%, rgba(20,224,176,0.025), transparent 50%), radial-gradient(circle at 70% 70%, rgba(167,139,250,0.025), transparent 50%)',
          backgroundSize: '200% 200%',
        }}
      />

      {/* Ambient gradient orbs — fixed for persistent backdrop */}
      <motion.div
        style={{ y: orbParallax1 }}
        className="pointer-events-none fixed -left-40 -top-32 z-0 h-[520px] w-[520px] rounded-full bg-[#a78bfa] opacity-[0.07] blur-[120px]"
      />
      <motion.div
        style={{ y: orbParallax2 }}
        className="pointer-events-none fixed -right-32 top-1/3 z-0 h-[560px] w-[560px] rounded-full bg-[#14e0b0] opacity-[0.065] blur-[130px]"
      />
      <motion.div
        style={{ y: orbParallax3 }}
        className="pointer-events-none fixed left-1/4 top-[70%] z-0 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-[#a78bfa] opacity-[0.035] blur-[100px]"
      />
      <motion.div
        style={{ y: orbParallax4 }}
        className="pointer-events-none fixed right-1/4 top-[85%] z-0 h-[360px] w-[360px] translate-x-1/2 rounded-full bg-[#14e0b0] opacity-[0.03] blur-[100px]"
      />
      {/* 5th orb — different parallax, violet lower-left */}
      <motion.div
        style={{ y: orbParallax5 }}
        className="pointer-events-none fixed -left-20 top-[55%] z-0 h-[300px] w-[300px] rounded-full bg-[#a78bfa] opacity-[0.03] blur-[90px]"
      />

      {/* Edge vignette */}
      <div
        className="pointer-events-none fixed inset-0 z-[4]"
        style={{ background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.35) 100%)' }}
      />

      {/* ── Hero ── */}
      <div ref={heroRef} className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: easeOut }}
          className="mb-7 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_2px_hsl(var(--primary)/0.35)]" />
          ClusterScope · Solana Intelligence
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.08, ease: easeOut }}
          className="max-w-3xl text-center text-[clamp(2.5rem,6vw,4rem)] font-extrabold leading-[1.06] tracking-tight text-foreground"
        >
          Uncover <span className="text-primary">Hidden</span>
          <br />
          Trading Networks on Solana
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.16, ease: easeOut }}
          className="mx-auto mt-6 max-w-[640px] text-center text-base text-muted-foreground sm:text-lg"
        >
          Real-time on-chain analysis that reveals coordinated wallet clusters, traces funding
          origins, and identifies behavioral fingerprints invisible to the naked eye.
        </motion.p>

        {/* Stats card — improved glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={heroInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.26, ease: easeOut }}
          className="mx-auto mt-8 w-full max-w-[520px] rounded-2xl border border-white/[0.10] bg-white/[0.04] px-2 py-2 shadow-[0_0_40px_-10px_rgba(20,224,176,0.04),inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-2xl"
        >
          <div className="grid grid-cols-3 divide-x divide-white/[0.08]">
            <CountUpStat {...dataset.landingStats.capitalTraced} delay={250} />
            <CountUpStat {...dataset.landingStats.walletsMapped} delay={400} />
            <CountUpStat {...dataset.landingStats.activeClusters} delay={550} />
          </div>
          <div className="mt-1 grid grid-cols-3 border-t border-white/[0.08]">
            <div className="col-span-1" />
            <CountUpStat {...dataset.landingStats.avgConfidence} delay={700} />
            <div className="col-span-1" />
          </div>
        </motion.div>

        {/* Feature pills */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.38, ease: easeOut }}
          className="mx-auto mt-6 flex max-w-2xl flex-wrap items-center justify-center gap-2.5"
        >
          {FEATURE_PILLS.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] px-3.5 py-1.5 text-xs text-muted-foreground/90 transition-colors duration-300 hover:border-primary/40 hover:text-foreground"
            >
              <Icon className="h-3.5 w-3.5 text-primary/80" />
              {label}
            </span>
          ))}
        </motion.div>

        {/* CTA button */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.48, ease: easeOut }}
          className="mt-10"
        >
          <motion.button
            onClick={onBeginAnalysis}
            whileHover={{ scale: 1.035 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: easeOut }}
            className="group relative inline-flex items-center gap-2 rounded-xl bg-[#14e0b0] px-8 py-3.5 text-sm font-bold text-[#04140f] shadow-[0_0_24px_-2px_rgba(20,224,176,0.30)] transition-shadow hover:shadow-[0_0_40px_2px_rgba(20,224,176,0.45)]"
          >
            Begin Analysis
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </motion.button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.58, ease: easeOut }}
          className="mt-4 text-xs text-muted-foreground/70"
        >
          Token Scanner · Wallet Profiler · Capital Trace
        </motion.p>
      </div>

      {/* ── Scrollable sections ── */}
      <div className="relative z-10">
        <HowItWorks />
        <ExampleAnalysis />
        <WhyClusterScope />
        <CoreFeatures />
        <FinalCTA onBeginAnalysis={onBeginAnalysis} />
      </div>
    </div>
  );
}
